import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr, fileUrl } from "../lib/api";
import { downscaleImage } from "../lib/img";
import { Check, Upload, Loader2, MapPin, ArrowRight, ArrowLeft, Mail, Compass, Sparkles as SparklesIcon } from "lucide-react";

const NAVY = "#0d2340";
const NAVY_DARK = "#0A1B33";
const GOLD = "#E0A73A";
const BG = "/assets/journey-bg.jpg";
const TOTAL = 6;

const PALETTES = [
  { name: "Gold & Deep Blue", color: "#C9973A" },
  { name: "Forest", color: "#3F7D5B" },
  { name: "Ocean", color: "#2A7DA3" },
  { name: "Sunset", color: "#E07A3F" },
  { name: "Berry", color: "#A63D6A" },
  { name: "Slate", color: "#5B6472" },
];

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const GoldSwoosh = ({ className = "w-40 h-3" }) => (
  <svg viewBox="0 0 200 12" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8 Q100 0 197 8" stroke={GOLD} strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

// Living background — Ken Burns drift on the photo + a drifting gold glow + floating gold dust.
function Particles() {
  const bits = useMemo(() => Array.from({ length: 10 }).map((_, i) => ({
    left: 6 + (i * 9.3) % 90, delay: (i * 1.6) % 10, dur: 9 + (i % 5) * 2.2, size: 3 + (i % 3),
  })), []);
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {bits.map((b, i) => (
        <span key={i} className="wiz-particle" style={{ left: `${b.left}%`, width: b.size, height: b.size, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s` }} />
      ))}
    </div>
  );
}

// Full-bleed premium shell — mountain journey photo + navy readability gradient + motion.
function Shell({ children }) {
  return (
    <div className="wizard-root relative w-full min-h-[100dvh] overflow-hidden" style={{ background: NAVY }}>
      <img src={BG} alt="" className="fixed inset-0 w-full h-full object-cover" style={{ objectPosition: "center 45%", filter: "saturate(1.15) brightness(1.06) contrast(1.02)" }} />
      <div className="fixed inset-0" style={{ background: "linear-gradient(180deg, rgba(13,35,64,0.50) 0%, rgba(13,35,64,0.12) 26%, rgba(13,35,64,0.06) 50%, rgba(10,27,51,0.78) 100%)" }} />
      <div className="sun-glow fixed left-1/2 top-[47%] pointer-events-none" style={{ width: 440, height: 440, marginTop: 0, borderRadius: "9999px", transform: "translate(-50%,-50%)", background: "radial-gradient(circle, rgba(255,220,150,0.6), rgba(255,185,95,0.22) 42%, rgba(255,185,95,0) 70%)", filter: "blur(16px)" }} />
      <Particles />
      <div className="relative z-10 min-h-[100dvh] w-full flex justify-center overflow-y-auto">
        <div className="w-full max-w-md px-5 flex flex-col" style={{ paddingTop: "calc(env(safe-area-inset-top) + 26px)", paddingBottom: "calc(env(safe-area-inset-bottom) + 26px)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Progress({ step }) {
  return (
    <div className="flex gap-1.5 mb-6" data-testid="wizard-progress">
      {Array.from({ length: TOTAL }).map((_, i) => (
        <div key={i} className="h-1.5 flex-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.18)" }}>
          <motion.div className="h-full rounded-full" style={{ background: GOLD }}
            initial={false} animate={{ width: i < step ? "100%" : "0%" }} transition={{ duration: 0.4 }} />
        </div>
      ))}
    </div>
  );
}

const inputCls = "w-full border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#E0A73A] focus:ring-1 focus:ring-[#E0A73A] transition-all";
const inputBg = { background: "rgba(255,255,255,0.08)" };
const labelCls = "block text-sm font-medium text-white/80 mb-1.5 text-left";

function Field({ label, testid, hint, style, ...props }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={testid} className={inputCls} style={{ ...inputBg, ...(style || {}) }} {...props} />
      {hint && <p className="text-[13px] text-white/55 mt-1.5 leading-snug">{hint}</p>}
    </div>
  );
}

// Card + heading used on every content step
function Card({ eyebrow, heading, description, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="rounded-[2rem] p-6 md:p-8 text-left shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
      style={{ background: "rgba(10,27,51,0.55)", backdropFilter: "blur(22px)", border: `1px solid ${GOLD}40` }}
    >
      {eyebrow && <div className="text-xs uppercase tracking-[0.22em] font-bold mb-2" style={{ color: GOLD }}>{eyebrow}</div>}
      <h2 className="font-heading font-extrabold text-white text-3xl leading-tight">{heading}</h2>
      <div className="my-3"><GoldSwoosh /></div>
      {description && <p className="text-white/80 text-base leading-relaxed mb-6">{description}</p>}
      {children}
    </motion.div>
  );
}

function PrimaryBtn({ children, testid, disabled, onClick }) {
  return (
    <motion.button whileTap={{ scale: 0.97 }} data-testid={testid} disabled={disabled} onClick={onClick}
      className="w-full rounded-full font-semibold text-lg py-3.5 flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
      style={{ background: GOLD, color: NAVY_DARK }}>
      {children}
    </motion.button>
  );
}

function GoldBurst() {
  const dots = useMemo(() => Array.from({ length: 14 }).map((_, i) => {
    const a = (i / 14) * Math.PI * 2;
    return { x: Math.cos(a) * 120, y: Math.sin(a) * 120, d: 0.02 * i };
  }), []);
  return (
    <div className="relative h-24 w-24 mx-auto">
      {dots.map((p, i) => (
        <motion.span key={i} className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full"
          style={{ background: GOLD }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
          animate={{ x: p.x, y: p.y, opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
          transition={{ duration: 1.1, delay: p.d, repeat: Infinity, repeatDelay: 0.6 }} />
      ))}
      <motion.div className="absolute inset-0 flex items-center justify-center"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.15 }}>
        <div className="h-20 w-20 rounded-full flex items-center justify-center" style={{ background: GOLD }}>
          <Check className="h-10 w-10" style={{ color: NAVY_DARK }} strokeWidth={3} />
        </div>
      </motion.div>
    </div>
  );
}

export default function Wizard() {
  const { user, org, refreshConfig, refreshOrg, applyAccent } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { localStorage.removeItem("mc_pending_wizard"); }, []);

  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [d, setD] = useState({
    org_name: org?.org_name || "", tagline: "", mission_statement: "",
    accent_color: org?.accent_color || "#C9973A", logo_file_id: org?.logo_file_id || null,
    reply_email: org?.email || user?.email || "",
    trip_name: "", start_date: "", end_date: "", origin_country: "", origin_city: "",
    currency: "USD", budget_goal: "", donate_url: "",
  });
  const set = (k) => (e) => setD((p) => ({ ...p, [k]: e.target.value }));
  const setColor = (color) => { setD((p) => ({ ...p, accent_color: color })); applyAccent(color); };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await downscaleImage(file, 512, 0.9);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setD((p) => ({ ...p, logo_file_id: data.file_id }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const go = (n) => { setDir(n > step ? 1 : -1); setStep(n); };

  const saveOrg = async () => {
    await api.put("/org", {
      org_name: d.org_name || undefined, tagline: d.tagline || undefined,
      mission_statement: d.mission_statement || undefined,
      accent_color: d.accent_color, logo_file_id: d.logo_file_id || undefined,
      email: d.reply_email.trim(),
    });
    applyAccent(d.accent_color);
    await refreshOrg();
  };

  // Advance from the email step: this is where org identity + reply email persist.
  const submitEmail = async () => {
    if (!EMAIL_RE.test(d.reply_email.trim())) return toast.error("Enter a valid email so replies reach you");
    setLoading(true);
    try { await saveOrg(); go(5); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const saveTrip = async (skip) => {
    setLoading(true);
    try {
      if (!skip) {
        await api.put("/config", {
          trip_name: d.trip_name || undefined, start_date: d.start_date || undefined,
          end_date: d.end_date || undefined, origin_country: d.origin_country || undefined,
          origin_city: d.origin_city || undefined, currency: d.currency,
          budget_goal_cents: d.budget_goal ? Math.round(parseFloat(d.budget_goal) * 100) : 0,
          donate_url: d.donate_url || undefined,
        });
        await refreshConfig();
      }
      go(6);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      {step > 1 && step < 6 && <Progress step={step} />}

      <div className="flex-1 flex flex-col justify-center">
        <AnimatePresence mode="wait" custom={dir}>
          {/* STEP 1 — Welcome */}
          {step === 1 && (
            <motion.div key="s1" data-testid="wizard-step-1"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.4 }}
              className="text-center">
              <div className="h-16 w-16 rounded-2xl mx-auto flex items-center justify-center shadow-lg" style={{ background: GOLD }}>
                <Compass className="h-8 w-8" style={{ color: NAVY_DARK }} />
              </div>
              <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-[1.05] mt-6" style={{ textShadow: "0 2px 22px rgba(0,0,0,0.65)" }}>Let's build your<br />mission home.</h1>
              <div className="flex justify-center my-4"><GoldSwoosh className="w-44 h-3" /></div>
              <p className="text-white/95 text-base leading-relaxed max-w-sm mx-auto" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>Your journey begins here. In a few warm steps we'll set up your organization so you can focus on what matters most — your mission.</p>
              <div className="mt-10">
                <PrimaryBtn testid="btn-begin-journey" onClick={() => go(2)}>Begin the journey <ArrowRight className="h-5 w-5" /></PrimaryBtn>
              </div>
            </motion.div>
          )}

          {/* STEP 2 — Identity */}
          {step === 2 && (
            <div key="s2" data-testid="wizard-step-2">
              <Card eyebrow="Step 1 of 4" heading="Who are you?" description="Establish your identity — this is how supporters will recognize your mission.">
                <div className="space-y-4" style={{ "--g": GOLD }}>
                  <div>
                    <label className={labelCls}>Logo</label>
                    <div className="mt-1 flex items-center gap-3">
                      {d.logo_file_id ? (
                        <img src={fileUrl(`/api/files/${d.logo_file_id}`, 200)} alt="logo" className="h-14 w-14 rounded-2xl object-cover" />
                      ) : (
                        <div className="h-14 w-14 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-xl" style={{ background: GOLD, color: NAVY_DARK }}>{(d.org_name || "M")[0]}</div>
                      )}
                      <label data-testid="logo-upload" className="flex items-center gap-2 cursor-pointer text-sm rounded-full border border-white/25 text-white/90 px-4 py-2 hover:bg-white/10 transition-colors">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" style={{ color: GOLD }} />}
                        {d.logo_file_id ? "Replace logo" : "Upload logo"}
                        <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
                      </label>
                    </div>
                  </div>
                  <Field label="Organization name" testid="input-org-name" value={d.org_name} onChange={set("org_name")} placeholder="ASM Ministries" />
                  <Field label="Tagline" testid="input-org-tagline" value={d.tagline} onChange={set("tagline")} placeholder="Restoring hope across nations" />
                  <div>
                    <label className={labelCls}>Mission statement</label>
                    <textarea data-testid="input-org-mission" className={inputCls + " min-h-[110px] resize-none"} style={inputBg} value={d.mission_statement} onChange={set("mission_statement")} placeholder="What is your organization's purpose?" />
                  </div>
                </div>
                <div className="mt-7">
                  <PrimaryBtn testid="btn-next-step-2" disabled={!d.org_name.trim()} onClick={() => go(3)}>Continue <ArrowRight className="h-5 w-5" /></PrimaryBtn>
                </div>
              </Card>
              <Reassure />
              <BackLink onClick={() => go(1)} />
            </div>
          )}

          {/* STEP 3 — Brand color */}
          {step === 3 && (
            <div key="s3" data-testid="wizard-step-3">
              <Card eyebrow="Step 2 of 4" heading="Choose your colors." description="Pick an accent that reflects your brand. It highlights buttons, links and progress across your mission space.">
                <div className="flex flex-wrap gap-3" data-testid="color-preset-grid">
                  {PALETTES.map((p) => (
                    <button key={p.color} aria-label={p.name} onClick={() => setColor(p.color)} title={p.name}
                      className="h-11 w-11 rounded-full flex items-center justify-center border-2 transition-transform hover:scale-110"
                      style={{ background: p.color, borderColor: d.accent_color.toLowerCase() === p.color.toLowerCase() ? "#fff" : "transparent" }}>
                      {d.accent_color.toLowerCase() === p.color.toLowerCase() && <Check className="h-4 w-4 text-white" />}
                    </button>
                  ))}
                  <label className="h-11 w-11 rounded-full border-2 border-dashed border-white/40 flex items-center justify-center cursor-pointer relative" title="Custom color">
                    <span className="text-white/70 text-lg">+</span>
                    <input type="color" data-testid="input-custom-color" value={d.accent_color} onChange={(e) => setColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </label>
                </div>
                <div className="mt-6 rounded-2xl p-4 flex items-center gap-3" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-9 w-9 rounded-full" style={{ background: d.accent_color }} />
                  <div className="text-white/80 text-sm">Live preview — <span style={{ color: d.accent_color }} className="font-semibold">this is your accent</span></div>
                </div>
                <div className="mt-7">
                  <PrimaryBtn testid="btn-next-step-3" onClick={() => go(4)}>Continue <ArrowRight className="h-5 w-5" /></PrimaryBtn>
                </div>
              </Card>
              <Reassure />
              <BackLink onClick={() => go(2)} />
            </div>
          )}

          {/* STEP 4 — Reply email (REQUIRED) */}
          {step === 4 && (
            <div key="s4" data-testid="wizard-step-4">
              <Card eyebrow="Step 3 of 4" heading="Where should replies go?"
                description="Every email your supporters receive is sent from our secure Mission Companion system, under your organization's name. But when someone replies — to a thank-you note or a dispatch — that reply is delivered straight to the address below.">
                <div style={{ "--g": GOLD }}>
                  <Field label="Reply-to / contact email" testid="input-reply-email" type="email" value={d.reply_email} onChange={set("reply_email")} placeholder="you@yourministry.org"
                    hint="We pre-filled your login email — change it if replies should go somewhere else (e.g. a shared team inbox)." />
                </div>
                <div className="mt-5 rounded-2xl p-4 flex items-start gap-3" style={{ background: "rgba(224,167,58,0.10)", border: `1px solid ${GOLD}33` }}>
                  <Mail className="h-5 w-5 shrink-0 mt-0.5" style={{ color: GOLD }} />
                  <p className="text-white/85 text-sm leading-relaxed">This keeps every conversation personal — supporters feel like they're writing directly to <span className="text-white font-medium">you</span>, not to an app.</p>
                </div>
                <div className="mt-7">
                  <PrimaryBtn testid="btn-next-step-4" disabled={loading} onClick={submitEmail}>{loading ? "Saving…" : <>Continue <ArrowRight className="h-5 w-5" /></>}</PrimaryBtn>
                </div>
              </Card>
              <Reassure />
              <BackLink onClick={() => go(3)} />
            </div>
          )}

          {/* STEP 5 — First trip */}
          {step === 5 && (
            <div key="s5" data-testid="wizard-step-5">
              <Card eyebrow="Step 4 of 4" heading="Your first mission." description="Sketch the essentials to get going. You can edit everything — and add countries, dates and more — anytime from your dashboard.">
                <div className="space-y-4" style={{ "--g": GOLD }}>
                  <Field label="Trip name" testid="input-trip-name" value={d.trip_name} onChange={set("trip_name")} placeholder="RESET 2026 Mission" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Start date" testid="input-trip-start" type="date" style={{ colorScheme: "dark" }} value={d.start_date} onChange={set("start_date")} />
                    <Field label="End date" testid="input-trip-end" type="date" style={{ colorScheme: "dark" }} value={d.end_date} onChange={set("end_date")} />
                  </div>
                  <div className="rounded-2xl p-3.5 space-y-3" style={{ background: "rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center gap-2 text-sm font-medium text-white"><MapPin className="h-4 w-4" style={{ color: GOLD }} /> Home base <span className="text-white/50 font-normal">(where the journey begins)</span></div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Home country" testid="input-trip-location" value={d.origin_country} onChange={set("origin_country")} placeholder="United States" />
                      <Field label="Home city" testid="input-trip-city" value={d.origin_city} onChange={set("origin_city")} placeholder="Orlando, FL" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Currency" testid="input-trip-currency" value={d.currency} onChange={set("currency")} placeholder="USD" />
                    <Field label="Budget goal" testid="input-trip-goal" type="number" value={d.budget_goal} onChange={set("budget_goal")} placeholder="45000" />
                  </div>
                  <Field label="Donation link (optional)" testid="input-trip-donate" value={d.donate_url} onChange={set("donate_url")} placeholder="https://yourministry.org/give" />
                </div>
                <div className="mt-7">
                  <PrimaryBtn testid="btn-save-trip" disabled={loading} onClick={() => saveTrip(false)}>{loading ? "Saving…" : <>Save & continue <ArrowRight className="h-5 w-5" /></>}</PrimaryBtn>
                  <button type="button" data-testid="btn-skip-trip" onClick={() => saveTrip(true)} disabled={loading} className="w-full mt-3 text-sm text-white/65 hover:text-white transition-colors">Skip for now — I'll set this up later</button>
                </div>
              </Card>
              <Reassure />
              <BackLink onClick={() => go(4)} />
            </div>
          )}

          {/* STEP 6 — Finish */}
          {step === 6 && (
            <motion.div key="s6" data-testid="wizard-step-6"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
              className="text-center">
              <GoldBurst />
              <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-[1.05] mt-6" style={{ textShadow: "0 2px 22px rgba(0,0,0,0.65)" }}>You're all set.</h1>
              <div className="flex justify-center my-4"><GoldSwoosh className="w-40 h-3" /></div>
              <p className="text-white/95 text-base leading-relaxed max-w-sm mx-auto flex items-center justify-center gap-2" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>
                <SparklesIcon className="h-4 w-4" style={{ color: GOLD }} /> Your mission space is ready. May the journey be blessed and fruitful.
              </p>
              <div className="mt-10">
                <PrimaryBtn testid="btn-enter-dashboard" onClick={() => navigate("/app")}>Enter your dashboard <ArrowRight className="h-5 w-5" /></PrimaryBtn>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Shell>
  );
}

function BackLink({ onClick }) {
  return (
    <button type="button" data-testid="wizard-back" onClick={onClick} className="mt-4 mx-auto flex items-center gap-1 text-[13px] text-white/65 hover:text-white transition-colors">
      <ArrowLeft className="h-3.5 w-3.5" /> Back
    </button>
  );
}

function Reassure() {
  return <p className="text-center text-white/60 text-[13px] mt-3">You can change any of this later in <span className="font-medium text-white/80">Settings</span>.</p>;
}
